import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';

import Redirect from '@swf/components/Redirect/Redirect';
import SportsDefaultTitle from '@swf/components/SportsDefaultTitle/SportsDefaultTitle';
import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import useTranslationValues from '@swf/core/hooks/useTranslationValues';
import type { WidgetFC } from '@swf/core/types/Widget';

import { TXTINT_BRAND_BetStars } from './SportsSeoWidget.messages.json';
import type { SportsSeoWidgetConfig } from './config';
import { sportsSeoWidgetModel, useSportsSeoWidget } from './model';

const SportsSeoWidget: WidgetFC<React.FC, SportsSeoWidgetConfig> = () => {
  const { formatMessage } = useRegulatoryIntl();
  const { widgetSelect, widgetConfig } = useSportsSeoWidget();
  const tokens = useSelector(widgetSelect.tokens);
  const hasPreloaded = useSelector(widgetSelect.hasPreloaded);
  const redirect = useSelector(widgetSelect.redirect);
  const alternates = useSelector(widgetSelect.alternates);

  const platformTokens = useTranslationValues();

  if (redirect) {
    return <Redirect url={redirect.url} statusCode={redirect.statusCode} />;
  }

  if (widgetConfig.apiPath && !hasPreloaded) {
    return <SportsDefaultTitle />;
  }

  const allTokens = { ...platformTokens, ...tokens };

  return (
    <Helmet>
      <title>
        {formatMessage(widgetConfig.titleTag, allTokens)} | {formatMessage(TXTINT_BRAND_BetStars)}
      </title>
      <meta
        name="description"
        content={String(formatMessage(widgetConfig.descriptionTag, allTokens))}
      />
      {alternates.map((alternateProps) => (
        <link key={alternateProps.hrefLang} rel="alternate" {...alternateProps} />
      ))}
    </Helmet>
  );
};

SportsSeoWidget.model = sportsSeoWidgetModel;

export default SportsSeoWidget;
